import axios from 'axios';
import type { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import type { ApiResponse, ApiError } from './types';

// Base URL for API calls - should be configurable via environment variables
const BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

// Create Axios instance with default configuration
export const apiClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies with cross-origin requests
});

// Request interceptor for custom headers (no auth token needed with cookie authentication)
apiClient.interceptors.request.use(
  config => {
    // Add any custom headers here if needed in the future
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling responses and errors
apiClient.interceptors.response.use(
  response => {
    return response;
  },
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

    // Handle 401 errors (unauthorized) by attempting to refresh session
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // With cookie auth, just call the refresh endpoint - the cookie will be sent automatically
        await axios.post(`${BASE_URL}/auth/refresh`, {}, { withCredentials: true });

        // If refresh successful, retry the original request
        return apiClient(originalRequest);
      } catch {
        // Refresh failed, redirect to appropriate login page based on current route
        // No longer clearing localStorage - auth state managed by Redux
        
        // Check if current route is superadmin route
        const currentPath = window.location.pathname;
        if (currentPath.startsWith('/superadmin')) {
          // Redirect to superadmin login
          window.location.href = '/superadmin/auth/login';
        } else {
          // Redirect to merchant login
          window.location.href = '/login';
        }
      }
    }

    // Handle other errors
    const apiError: ApiError = {
      success: false,
      message: error.response?.data?.message || error.message || 'An error occurred',
      errors: error.response?.data?.errors || [],
      status: error.response?.status,
    };

    return Promise.reject(apiError);
  }
);

// Generic API request method
export const apiRequest = async <T = unknown>(config: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  const response = await apiClient(config);
  return response.data;
};

// Convenience methods for different HTTP verbs
export const api = {
  get: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'GET', url }),

  post: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'POST', url, data }),

  put: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'PUT', url, data }),

  patch: <T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'PATCH', url, data }),

  delete: <T = unknown>(url: string, config?: AxiosRequestConfig) =>
    apiRequest<T>({ ...config, method: 'DELETE', url }),
};
